import React, { useState } from 'react';
import { Box, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useServices } from '@/components/ServiceContext';
import { ServiceDataGrid } from '@/components/ServiceDataGrid';
import { boughtListFilters } from '@/pages/BoughtListFilterPage.tsx';
import { houseListFilters } from '@/pages/HouseListFilterPage.tsx';
import { FilterDefinitionGridColDefMapper } from '@lambda-layer/mappers/FilterDefinitionGridColDefMapper';
import { applyDefaultFilterOperators } from '@/utils/filter.ts';
import { staffSeedReportDefinitions } from '@lambda-layer/constants/reportDefinitions';

const filterDefinitionGridColDefMapper = new FilterDefinitionGridColDefMapper();

const DataExplorerPage: React.FC = () => {
  const { boughtListService, houseListService, staffSeedService } =
    useServices();

  // State to select between 'bought', 'house' and 'staff' list
  const [selectedListType, setSelectedListType] = useState<
    'bought' | 'house' | 'staff'
  >('bought');

  const handleListTypeChange = (
    _: React.MouseEvent<HTMLElement>,
    newListType: 'bought' | 'house' | 'staff'
  ) => {
    if (newListType !== null) {
      setSelectedListType(newListType);
    }
  };

  // Choose the appropriate service and columns based on the selection
  const service =
    selectedListType === 'bought'
      ? boughtListService
      : selectedListType === 'house'
        ? houseListService
        : staffSeedService;
  const filters =
    selectedListType === 'bought'
      ? boughtListFilters
      : selectedListType === 'house'
        ? houseListFilters
        : staffSeedReportDefinitions;
  const columns = [
    { field: 'id', headerName: 'ID', width: 300 },
    ...filterDefinitionGridColDefMapper.map(filters, {
      includeZeroOrderFields: true,
    }),
  ];

  /**
   * Defines the quick search fields for the data grid.
   */
  const quickSearchFields = applyDefaultFilterOperators(
    filterDefinitionGridColDefMapper.map(filters, {
      customFilter: fd => fd.databaseMetadata?.dataType === 'string',
    })
  ).map(col => col.field);

  return (
    <Box sx={{ p: 2 }}>
      <ToggleButtonGroup
        value={selectedListType}
        exclusive
        onChange={handleListTypeChange}
        aria-label="data list type"
        sx={{ mb: 2 }}
      >
        <ToggleButton value="bought" aria-label="bought list">
          Bought List
        </ToggleButton>
        <ToggleButton value="house" aria-label="house list">
          House List
        </ToggleButton>
        <ToggleButton value="staff" aria-label="staff seed list">
          Staff Seed List
        </ToggleButton>
      </ToggleButtonGroup>
      <ServiceDataGrid
        service={service}
        columns={columns}
        pageSize={10}
        quickSearchFields={['id', ...quickSearchFields]}
      />
    </Box>
  );
};

export default DataExplorerPage;
